import {
  Box,
  Theme,
  SxProps,
  Divider,
  Typography,
  IconButton,
  BoxProps,
} from "@mui/material";
import { Icon } from "../Icon";
import { forwardRef } from "react";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
  FONT_SIZES,
} from "../../constants/styles";

export type CardHeaderSize = "small" | "medium" | "large";

/**
 * 이미지/지도 카드 상단의 헤더 영역 props — 파일명·이전/다음·북마크·전체화면 액션.
 *
 * 부모(`Card`/`FloatImageViewer`)가 drag handle 영역을 지정하기 위해 `dragCancelId` 로 drag 제외 영역을 노출한다.
 */
export interface CardHeaderProps extends Omit<BoxProps, "ref"> {
  fileName?: string;
  size?: CardHeaderSize;
  onPrevious?: () => void;
  onNext?: () => void;
  onClose?: () => void;
  onHandle?: () => void;
  onBookmark?: () => void;
  onExpand?: () => void;
  bookmarked?: boolean;
  showNavigation?: boolean;
  /** drag 가 동작하면 안 되는 자식 영역의 DOM id (Card 가 부여) */
  dragCancelId?: string;
  hasNext?: boolean;
  hasPrevious?: boolean;
  sx?: SxProps<Theme>;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  (
    {
      fileName = "fileName",
      size = "medium",
      onPrevious,
      onNext,
      onClose,
      onHandle,
      onBookmark,
      onExpand,
      bookmarked = false,
      showNavigation = true,
      dragCancelId,
      hasNext = true,
      hasPrevious = true,
      sx,
      ...props
    },
    ref
  ) => {
    const isSmall = size === "small";
    const isMedium = size === "medium";
    const isLarge = size === "large";
    return (
      <Box
        ref={ref}
        sx={{
          ...sizeStyles[size],
          ...imageCardHeaderStyles.container,
          ...sx,
        }}
        {...props}
      >
        {isSmall && (
          <Typography variant="body2" sx={imageCardHeaderStyles.smallText}>
            {fileName}
          </Typography>
        )}
        {isMedium && (
          <>
            <IconButton size="small" onClick={onHandle}>
              <Icon name="20/etc/handle" size={20} clickable />
            </IconButton>
            {showNavigation && (
              <>
                <IconButton
                  size="small"
                  onClick={onPrevious}
                  disabled={!hasPrevious}
                  className={dragCancelId}
                  sx={{ opacity: !hasPrevious ? 0.5 : 1 }}
                >
                  <Icon name="20/arrow/left" size={20} clickable />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={imageCardHeaderStyles.mediumText}
                >
                  {fileName}
                </Typography>
                <IconButton
                  size="small"
                  onClick={onNext}
                  disabled={!hasNext}
                  className={dragCancelId}
                  sx={{ opacity: !hasNext ? 0.5 : 1 }}
                >
                  <Icon name="20/arrow/right" size={20} clickable />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={onClose}
                  className={dragCancelId}
                >
                  <Icon name="20/etc/close" size={20} clickable />
                </IconButton>
              </>
            )}
          </>
        )}
        {isLarge && (
          <>
            <IconButton
              size="small"
              onClick={onPrevious}
              disabled={!hasPrevious}
            >
              <Icon name="16/arrow/left" size={24} color="white" clickable />
            </IconButton>
            <Typography variant="body1" sx={imageCardHeaderStyles.largeText}>
              {fileName}
            </Typography>
            <IconButton size="small" onClick={onNext} disabled={!hasNext}>
              <Icon name="16/arrow/right" size={24} color="white" clickable />
            </IconButton>
            <Divider
              orientation="vertical"
              flexItem
              sx={imageCardHeaderStyles.divider}
              color="rgba(255, 255, 255, 0.5)"
            />
            <IconButton size="small" onClick={onBookmark}>
              {bookmarked ? (
                <Icon name="etc/BookmarkActive" size={24} clickable />
              ) : (
                <Icon
                  name="etc/Bookmark"
                  size={24}
                  sx={imageCardHeaderStyles.bookmarkIcon}
                  clickable
                />
              )}
            </IconButton>
            <Divider
              orientation="vertical"
              flexItem
              sx={imageCardHeaderStyles.divider}
              color="rgba(255, 255, 255, 0.5)"
            />
            <IconButton size="small" onClick={onExpand}>
              <Icon name="etc/exit screen" size={24} color="white" clickable />
            </IconButton>
          </>
        )}
      </Box>
    );
  }
);

CardHeader.displayName = "CardHeader";

const sizeStyles = {
  small: {
    width: "136px",
    minHeight: "20px",
    padding: `${SPACING_CONSTANTS.spacing[1]}px ${SPACING_CONSTANTS.spacing[2]}px`,
    fontSize: FONT_SIZES.sm,
    fontWeight: 500,
    lineHeight: "12px",
    justifyContent: "center",
    color: "rgba(255, 255, 255, 0.5)",
    backgroundColor: "rgba(0, 0, 0, 0.56)",
  },
  medium: {
    width: "272px",
    minHeight: "36px",
    padding: `0 ${SPACING_CONSTANTS.spacing[2]}px`,
    borderRadius: SPACING_CONSTANTS.borderRadius.sm,
    backgroundColor: COLOR_CONSTANTS.common.white_states.main,
    fontSize: FONT_SIZES.md,
    fontWeight: 500,
    color: COLOR_CONSTANTS.text.primary,
  },
  large: {
    width: "363px",
    minHeight: "40px",
    padding: `0 ${SPACING_CONSTANTS.spacing[3]}px`,
    borderRadius: SPACING_CONSTANTS.borderRadius.lg,
    backgroundColor: COLOR_CONSTANTS._components.backdrop.fill,
    fontSize: FONT_SIZES.lg,
    fontWeight: 500,
    color: COLOR_CONSTANTS.common.white_states.main,
  },
};

const imageCardHeaderStyles = {
  container: {
    display: "flex",
    alignItems: "center",
  },
  smallText: {
    textAlign: "center",
    overflow: "auto",
    whiteSpace: "nowrap",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  mediumText: {
    flex: 1,
    textAlign: "center",
    overflow: "auto",
    whiteSpace: "nowrap",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  largeText: {
    flex: 1,
    textAlign: "center",
    overflow: "auto",
    whiteSpace: "nowrap",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
  },
  divider: {
    mx: 1,
  },
  bookmarkIcon: {
    filter: "brightness(0) invert(1)",
  },
} as const;
